<template>
  <div class="first:*:mt-4 lg:first:*:mt-0 lg:mt-2 grid gap-y-1">
    <div v-if="groupedQuotes.valueProps" class="flex flex-wrap gap-1">
      <ProviderLabel
        v-for="valueProp in groupedQuotes.valueProps"
        :key="valueProp"
        type="good"
        :provider="groupedQuotes.psp"
        :message-key="`provider_messages:${valueProp}`"
      />
    </div>

    <ProviderPromotion
      v-if="commonPromotion"
      :provider-display-name="groupedQuotes.psp.displayName"
      :provider-logo="groupedQuotes.psp.logo.sm"
      :promotion="commonPromotion"
      @open="sendPromoAnalytics"
    >
      <template #link>
        <ProviderLink
          v-if="isRecommended"
          class="mx-auto mb-4"
          :provider="groupedQuotes.psp"
          :promotion="commonPromotion"
          location="promotion_modal"
        />
      </template>
    </ProviderPromotion>
    <ProviderPromotion
      v-else-if="hasAnyPromos"
      class="lg:hidden"
      :provider-display-name="groupedQuotes.psp.displayName"
      :provider-logo="groupedQuotes.psp.logo.sm"
      @open="sendPromoAnalytics"
    >
      <span class="font-semibold"> {{ $t('promotion.has_promotions') }} </span>
    </ProviderPromotion>

    <div v-if="groupedQuotes.customMessages.information" class="flex flex-wrap gap-1">
      <ProviderLabel
        v-for="infoMessage in groupedQuotes.customMessages.information"
        :key="infoMessage"
        type="info"
        :provider="groupedQuotes.psp"
        :message-key="`provider_messages:${infoMessage}`"
      />
    </div>

    <div
      v-if="groupedQuotes.customMessages.caution || !isSupportedLang"
      class="flex flex-wrap gap-1"
    >
      <ProviderLabel
        v-for="cautionMessage in groupedQuotes.customMessages.caution"
        :key="cautionMessage"
        type="caution"
        :provider="groupedQuotes.psp"
        :message-key="`provider_messages:${cautionMessage}`"
      />

      <ProviderLabel
        v-if="!isSupportedLang"
        type="caution"
        :provider="groupedQuotes.psp"
        :message-key="'transfer_comparison.not_available_in_language'"
        :details-text="availableInLanguages"
      />
    </div>

    <div v-if="groupedQuotes.customMessages.warning" class="flex flex-wrap gap-1">
      <ProviderLabel
        v-for="warningMessage in groupedQuotes.customMessages.warning"
        :key="warningMessage"
        type="warning"
        :provider="groupedQuotes.psp"
        :message-key="`provider_messages:${warningMessage}`"
      />
    </div>

    <ProviderLabel
      v-if="!isRecommended"
      class="xl:hidden"
      type="warning"
      :provider="groupedQuotes.psp"
    >
      <div class="text-12 lg:text-14">
        <strong>{{ $t('transfer_comparison.not_recommended') }}</strong>
        {{ $t('transfer_comparison.poor_provider') }}
        <a
          v-if="groupedQuotes.psp.slug === 'paypal'"
          class="hidden lg:inline link"
          target="_blank"
          rel="noopener"
          :href="groupedQuotes.psp.url ?? undefined"
        >
          {{ $t('read_full_review') }}
        </a>
      </div>
    </ProviderLabel>

    <ProviderLabel
      v-if="payoutNetworks"
      type="info"
      :provider="groupedQuotes.psp"
      :details-text="payoutNetworks.long"
    >
      <span class="font-semibold">{{ payoutNetworks.short }}</span>
    </ProviderLabel>
  </div>
</template>

<script lang="ts" setup>
import type { GroupedQuotes, Promotion } from './types'
import { isRecommendedProvider } from './helpers/recommended'
import ProviderLabel from './provider-label.vue'
import ProviderLink from './provider-link.vue'
import { Comparison } from './analytics/objects/Comparison'
import { ComparisonSection } from './analytics/objects/ComparisonSection'
import { ComparisonResult } from './analytics/objects/ComparisonResult'
import { panelOpen } from './analytics/actions'
import type { PaymentType } from '@core/types/imt'

const props = defineProps<{
  groupedQuotes: GroupedQuotes
  commonPromotion: Promotion | null
}>()

const { $track, $lang, $t } = useNuxtApp()
const tabType = inject<Ref<PaymentType>>('activeTabType')

const getResultAnalyticsObj = inject('getResultAnalyticsObj') as (
  slug: string,
  location?: string,
) => ComparisonResult | undefined
const sectionAnalyticsObj = inject<Ref<ComparisonSection>>('sectionAnalyticsObj')
const comparisonAnalyticsObj = inject<Ref<Comparison>>('comparisonAnalyticsObj')

const hasAnyPromos = computed(() => {
  return props.groupedQuotes.services.some((s) => s.promotion)
})

const isSupportedLang = computed(() => {
  return props.groupedQuotes.psp.languages.some((l) => l.code === $lang)
})

const availableInLanguages = computed(() => {
  if (isSupportedLang.value) return ''
  return $t('transfer_comparison.available_in_languages', {
    provider: props.groupedQuotes.psp.displayName,
    languages: props.groupedQuotes.psp.languages.map((l) => l.label).join(', '),
    count: props.groupedQuotes.psp.languages.length,
  })
})

const isRecommended = computed(() => {
  return isRecommendedProvider(props.groupedQuotes.psp)
})

const validPayoutNetworks = computed(() =>
  props.groupedQuotes.psp.payoutNetworks
    .filter((pon) =>
      pon.payouts.some((po) => props.groupedQuotes.services.some((s) => s.quote.payout === po)),
    )
    .sort((a, b) => (b.numberOfLocations ?? -1) - (a.numberOfLocations ?? -1))
    .map((pon) => pon.name),
)

const PAYOUT_NETWORKS_MORE = 5
const PAYOUT_NETWORKS_MORE_PICK = 3
const payoutNetworks = computed(() => {
  if (validPayoutNetworks.value.length === 0) {
    return false
  }

  const shorten = validPayoutNetworks.value.length > PAYOUT_NETWORKS_MORE

  const displayNames = shorten
    ? validPayoutNetworks.value.slice(0, PAYOUT_NETWORKS_MORE_PICK)
    : validPayoutNetworks.value
  const translationKey = tabType?.value === 'cash' ? 'cash' : 'default'

  return {
    short: `${$t(`payout_network.${translationKey}`, {
      payout_networks: displayNames,
    })} ${
      shorten
        ? $t('payout_network.more', {
            count: validPayoutNetworks.value.length - PAYOUT_NETWORKS_MORE_PICK,
          })
        : ''
    }`,
    long: shorten
      ? $t(`payout_network.${translationKey}`, {
          payout_networks: validPayoutNetworks.value,
        })
      : undefined,
  }
})

async function sendPromoAnalytics() {
  await panelOpen(
    $track,
    'Promotion Details',
    comparisonAnalyticsObj?.value,
    sectionAnalyticsObj?.value,
    getResultAnalyticsObj(props.groupedQuotes.psp.slug),
  )
}
</script>
